import { useCallback, useEffect, useRef, useState } from "react";
import AIKeySources from "@/components/admin/AIKeySources";
import { Users, Sparkles, MessageSquare, Building2, CalendarClock, MessagesSquare, Loader2, TrendingUp, Download, Coins, Bot } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CARDS = [
  { key: "businesses", label: "Total Bisnis", icon: Building2, color: "#2563EB" },
  { key: "leads", label: "Total Leads", icon: Users, color: "#10B981" },
  { key: "sessions", label: "Percakapan", icon: MessagesSquare, color: "#F59E0B" },
  { key: "contents", label: "Konten Dibuat", icon: Sparkles, color: "#A855F7" },
  { key: "messages", label: "Total Pesan", icon: MessageSquare, color: "#06B6D4" },
  { key: "scheduled_pending", label: "Jadwal Menunggu", icon: CalendarClock, color: "#F43F5E" },
];

export default function Dashboard({ businesses = [], role, onEditBusinessKey }) {
  const allBizLabel = role === "super_admin" ? "Semua Bisnis" : "Semua Bisnis Saya";
  const [data, setData] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [trends, setTrends] = useState(null);
  const [savings, setSavings] = useState(null);
  const [savingsTrend, setSavingsTrend] = useState(null);
  const [savingsPeriod, setSavingsPeriod] = useState("week");
  const [weeks, setWeeks] = useState("8");
  const [bizId, setBizId] = useState("all");
  const chartRef = useRef(null);

  const exportReport = async (type) => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, { backgroundColor: "#FFFFFF", scale: 2 });
      if (type === "png") {
        const link = document.createElement("a");
        link.download = "laporan-tren.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.success("Gambar laporan diunduh");
        return;
      }
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const bizLabel = bizId === "all" ? allBizLabel : (businesses.find((b) => b.id === bizId)?.name || "-");
      pdf.setFontSize(16); pdf.setTextColor(30);
      pdf.text("Laporan Tren - ContentChatAI", 40, 42);
      pdf.setFontSize(9); pdf.setTextColor(120);
      pdf.text(`Dibuat: ${new Date().toLocaleString("id-ID")}  |  Rentang: ${weeks} minggu  |  Bisnis: ${bizLabel}`, 40, 58);
      const imgW = pageW - 80;
      const imgH = (canvas.height * imgW) / canvas.width;
      pdf.addImage(img, "PNG", 40, 72, imgW, imgH);
      let y = 72 + imgH + 24;
      autoTable(pdf, {
        startY: y,
        head: [["Minggu", "Leads", "Percakapan"]],
        body: (trends || []).map((t) => [t.label, String(t.leads), String(t.sessions)]),
        theme: "grid", headStyles: { fillColor: [37, 99, 235] }, styles: { fontSize: 9 },
      });
      autoTable(pdf, {
        startY: pdf.lastAutoTable.finalY + 20,
        head: [["Bisnis", "Leads", "Percakapan", "Konten"]],
        body: (data?.per_business || []).map((b) => [b.name, String(b.leads), String(b.sessions), String(b.contents)]),
        theme: "grid", headStyles: { fillColor: [16, 185, 129] }, styles: { fontSize: 9 },
      });
      if (savings && savings.total > 0) {
        pdf.setFontSize(12); pdf.setTextColor(30);
        pdf.text("Ringkasan Penghematan Token AI", 40, pdf.lastAutoTable.finalY + 28);
        autoTable(pdf, {
          startY: pdf.lastAutoTable.finalY + 36,
          head: [["Metrik", "Nilai"]],
          body: [
            ["Balasan tanpa AI", `${savings.saved_pct}% (${savings.saved} dari ${savings.total})`],
            ["Dijawab FAQ", String(savings.faq)],
            ["Jawaban umum (fallback)", String(savings.fallback)],
            ["Pakai AI", String(savings.ai)],
            ["Estimasi token dihemat", `${(savings.est_tokens_saved / 1000).toFixed(1)}k token`],
            ["Estimasi hemat biaya", `Rp${Number(savings.est_rupiah_saved || 0).toLocaleString("id-ID")} (~Rp${Number(savings.est_rupiah_per_month || 0).toLocaleString("id-ID")}/bln)`],
          ],
          theme: "grid", headStyles: { fillColor: [168, 85, 247] }, styles: { fontSize: 9 },
        });
      }
      pdf.save("laporan-tren.pdf");
      toast.success("PDF laporan lengkap diunduh");
    } catch (e) {
      toast.error("Gagal mengekspor laporan");
    }
  };

  const loadStats = useCallback(async () => {
    setStatsError("");
    try {
      const response = await api.get("/stats");
      setData(response.data);
    } catch (error) {
      setStatsError(apiError(error));
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    const params = bizId !== "all" ? { business_id: bizId } : {};
    api.get("/stats/token-savings", { params }).then((r) => setSavings(r.data)).catch(() => {});
  }, [bizId]);

  useEffect(() => {
    const params = { period: savingsPeriod };
    if (bizId !== "all") params.business_id = bizId;
    api.get("/stats/savings-trends", { params }).then((r) => setSavingsTrend(r.data)).catch(() => {});
  }, [bizId, savingsPeriod]);

  useEffect(() => {
    const params = { weeks };
    if (bizId !== "all") params.business_id = bizId;
    api.get("/stats/trends", { params }).then((r) => setTrends(r.data)).catch(() => {});
  }, [weeks, bizId]);

  if (!data) {
    if (statsError) {
      return <div role="alert" data-testid="stats-load-error" className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 space-y-3">
        <p className="text-red-700">Statistik gagal dimuat: {statsError}</p>
        <Button type="button" onClick={loadStats} variant="outline">Coba lagi</Button>
      </div>;
    }
    return <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Memuat statistik...</div>;
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {CARDS.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-5 hover:border-[#3B82F6] transition-colors">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${c.color}22` }}>
              <c.icon size={20} style={{ color: c.color }} />
            </div>
            <p className="font-heading text-3xl font-black">{data.totals[c.key]}</p>
            <p className="text-xs text-slate-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <AIKeySources businesses={businesses} role={role} onEditBusinessKey={onEditBusinessKey} />
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6" data-testid="trends-chart" ref={chartRef}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-[#2563EB]" />
            <h3 className="font-heading font-bold">Tren Leads & Percakapan</h3>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Select value={bizId} onValueChange={setBizId}>
              <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] w-full sm:w-[180px] h-9 text-xs" data-testid="trends-business-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                <SelectItem value="all">{allBizLabel}</SelectItem>
                {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={weeks} onValueChange={setWeeks}>
              <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] w-full sm:w-[120px] h-9 text-xs" data-testid="trends-weeks-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                <SelectItem value="4">4 minggu</SelectItem>
                <SelectItem value="8">8 minggu</SelectItem>
                <SelectItem value="12">12 minggu</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-report-button" variant="outline" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl h-9 text-xs" data-html2canvas-ignore>
                  <Download size={14} className="mr-1" /> Ekspor
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                <DropdownMenuItem onClick={() => exportReport("png")} data-testid="export-png" className="cursor-pointer focus:bg-[#F1F5F9]">Unduh Gambar (PNG)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportReport("pdf")} data-testid="export-pdf" className="cursor-pointer focus:bg-[#F1F5F9]">Unduh PDF Lengkap</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trends || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, color: "#0F172A" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="leads" name="Leads" stroke="#10B981" strokeWidth={2} fill="url(#gLeads)" />
              <Area type="monotone" dataKey="sessions" name="Percakapan" stroke="#2563EB" strokeWidth={2} fill="url(#gSessions)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#FFFFFF] to-[#F1F5F9] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6" data-testid="token-savings-card">
        <div className="flex items-center gap-2 mb-5">
          <Coins size={18} className="text-[#059669]" />
          <h3 className="font-heading font-bold">Penghematan Token AI</h3>
          {bizId !== "all" && <span className="text-xs text-slate-500">· {businesses.find((b) => b.id === bizId)?.name}</span>}
        </div>
        {!savings || savings.total === 0 ? (
          <p className="text-sm text-slate-500" data-testid="token-savings-empty">Belum ada percakapan chatbot. Statistik penghematan akan muncul setelah ada aktivitas chat.</p>
        ) : (
          <div className="grid sm:grid-cols-4 gap-4">
            <div className="sm:col-span-1 flex flex-col items-center justify-center bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4" data-testid="token-savings-pct">
              <p className="font-heading text-4xl font-black text-[#059669]">{savings.saved_pct}%</p>
              <p className="text-xs text-slate-600 mt-1 text-center">balasan tanpa AI</p>
            </div>
            <div className="sm:col-span-3 grid grid-cols-2 xl:grid-cols-5 gap-3">
              <div className="bg-[#F1F5F9] rounded-xl p-3" data-testid="token-savings-faq">
                <div className="flex items-center gap-1.5 text-[#059669] mb-1"><MessageSquare size={14} /><span className="text-xs">FAQ</span></div>
                <p className="font-heading text-xl font-bold">{savings.faq}</p>
              </div>
              <div className="bg-[#F1F5F9] rounded-xl p-3" data-testid="token-savings-fallback">
                <div className="flex items-center gap-1.5 text-[#B45309] mb-1"><MessagesSquare size={14} /><span className="text-xs">Jawaban Umum</span></div>
                <p className="font-heading text-xl font-bold">{savings.fallback}</p>
              </div>
              <div className="bg-[#F1F5F9] rounded-xl p-3" data-testid="token-savings-ai">
                <div className="flex items-center gap-1.5 text-[#2563EB] mb-1"><Bot size={14} /><span className="text-xs">Pakai AI</span></div>
                <p className="font-heading text-xl font-bold">{savings.ai}</p>
              </div>
              <div className="bg-[#F1F5F9] rounded-xl p-3" data-testid="token-savings-tokens">
                <div className="flex items-center gap-1.5 text-[#A855F7] mb-1"><Coins size={14} /><span className="text-xs">Est. Token Hemat</span></div>
                <p className="font-heading text-xl font-bold">{(savings.est_tokens_saved / 1000).toFixed(1)}k</p>
              </div>
              <div className="bg-[#F1F5F9] rounded-xl p-3" data-testid="token-savings-rupiah">
                <div className="flex items-center gap-1.5 text-[#059669] mb-1"><Coins size={14} /><span className="text-xs">Est. Hemat (Rp)</span></div>
                <p className="font-heading text-xl font-bold">Rp{Number(savings.est_rupiah_saved || 0).toLocaleString("id-ID")}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">≈ Rp{Number(savings.est_rupiah_per_month || 0).toLocaleString("id-ID")}/bln</p>
              </div>
            </div>
          </div>
        )}

        {savingsTrend && savingsTrend.some((d) => d.saved || d.ai) && (
          <div className="mt-6 pt-5 border-t border-[#E2E8F0]" data-testid="savings-trend">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <p className="text-sm text-slate-700">Tren penghematan dari waktu ke waktu</p>
              <div className="flex bg-[#F1F5F9] rounded-lg p-0.5">
                <button onClick={() => setSavingsPeriod("week")} data-testid="savings-period-week" className={`px-3 py-1 text-xs rounded-md transition-colors ${savingsPeriod === "week" ? "bg-[#2563EB] text-white" : "text-slate-600 hover:text-slate-900"}`}>Mingguan</button>
                <button onClick={() => setSavingsPeriod("month")} data-testid="savings-period-month" className={`px-3 py-1 text-xs rounded-md transition-colors ${savingsPeriod === "month" ? "bg-[#2563EB] text-white" : "text-slate-600 hover:text-slate-900"}`}>Bulanan</button>
              </div>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={savingsTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, color: "#0F172A" }} cursor={{ fill: "#E2E8F055" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="saved" name="Tanpa AI (hemat)" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="ai" name="Pakai AI" stackId="a" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6">
          <h3 className="font-heading font-bold mb-4">Ringkasan per Bisnis</h3>
          {data.per_business.length === 0 ? (
            <p className="text-sm text-slate-500">Belum ada bisnis.</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-4 text-xs uppercase tracking-wider text-slate-500 px-3">
                <span className="col-span-1">Bisnis</span><span className="text-center">Leads</span><span className="text-center">Chat</span><span className="text-center">Konten</span>
              </div>
              {data.per_business.map((b) => (
                <div key={b.id} className="grid grid-cols-4 items-center bg-[#F1F5F9] rounded-lg px-3 py-2.5 text-sm">
                  <span className="truncate font-medium">{b.name}</span>
                  <span className="text-center text-[#059669]">{b.leads}</span>
                  <span className="text-center text-[#B45309]">{b.sessions}</span>
                  <span className="text-center text-[#A855F7]">{b.contents}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6">
          <h3 className="font-heading font-bold mb-4">Leads Terbaru</h3>
          {data.recent_leads.length === 0 ? (
            <p className="text-sm text-slate-500">Belum ada lead.</p>
          ) : (
            <div className="space-y-2">
              {data.recent_leads.map((l) => (
                <div key={l.id} className="flex items-center justify-between bg-[#F1F5F9] rounded-lg px-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{l.name}</p>
                    <p className="text-xs text-slate-500 truncate">{l.business_name} · {l.whatsapp}</p>
                  </div>
                  <span className="text-xs text-slate-600 shrink-0">{new Date(l.created_at).toLocaleDateString("id-ID")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
